import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import { Textarea, FileUpload, Dropdown, employeeID, companyID, sanitizeLabName, normalizeSpaces, CancelButton } from "../../../Common/UIComponents";
import {
  saveFacility,
  fetchFacilities,
  fetchCityList,
} from "../../../API_Service/Settings_Slice";
import { URL } from "../../../Common/APIURL";

const NAME_MAX_LENGTH = 250;
const DESC_MAX_LENGTH = 500;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toOptions = (items = [], labelKey, valueKey) =>
  items.map((item) => ({
    label: item[labelKey] ?? item.Label ?? item.Name ?? item.name ?? String(item),
    value: item[valueKey] ?? item.Value ?? item.ID ?? item.id ?? String(item),
  }));

const EMPTY_FORM = {
  name: "",
  cityId: "",
  tempMin: "",
  tempMax: "",
  humidMin: "",
  humidMax: "",
  description: "",
  image: null,
};

// ─────────────────────────────────────────────
// FIELD COMPONENTS
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-bold text-[#181c1d] ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

const TextInput = ({ placeholder, value, onChange, error, maxLength }) => (
  <div>
    <input
      type="text"
      value={value}
      onChange={(e) => { const v = e.target.value.replace(/^\s+/, ""); onChange({ ...e, target: { ...e.target, value: v } }); }}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`w-full bg-[#e5e9e9] border-none rounded-xl px-4 py-2.5 text-[#181c1d]
                  placeholder:text-[#3e494a]/40 text-sm
                  focus:ring-2 focus:ring-[#08757c] focus:bg-white transition-all outline-none
                  ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}
    />
    {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
  </div>
);

const clampValue = (val, min, max) => {
  if (val === "" || val === "-") return val;
  const n = Number(val);
  if (isNaN(n)) return val;
  if (max !== undefined && n > max) return String(max);
  if (min !== undefined && n < min) return String(min);
  return val;
};

const NumberRangeInput = ({ minValue, maxValue, onMinChange, onMaxChange, unit, minError, maxError, rangeMin, rangeMax }) => {
  const wrap = (handler) => (e) => {
    const clamped = clampValue(e.target.value, rangeMin, rangeMax);
    handler({ target: { value: clamped } });
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <input
            type="number"
            value={minValue}
            onChange={wrap(onMinChange)}
            placeholder="Min"
            min={rangeMin}
            max={rangeMax}
            className={`w-full bg-[#e5e9e9] border-none rounded-xl px-4 py-4 pr-12 text-[#181c1d]
                        placeholder:text-[#3e494a]/40 text-sm focus:ring-2 focus:ring-[#08757c]
                        focus:bg-white transition-all outline-none
                        ${minError ? "ring-2 ring-[#ba1a1a]" : ""}`}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#3e494a]/50 font-bold pointer-events-none">
            {unit}
          </span>
        </div>
        <span className="text-[#3e494a] font-bold text-sm flex-shrink-0">to</span>
        <div className="relative flex-1">
          <input
            type="number"
            value={maxValue}
            onChange={wrap(onMaxChange)}
            placeholder="Max"
            min={rangeMin}
            max={rangeMax}
            className={`w-full bg-[#e5e9e9] border-none rounded-xl px-4 py-4 pr-12 text-[#181c1d]
                        placeholder:text-[#3e494a]/40 text-sm focus:ring-2 focus:ring-[#08757c]
                        focus:bg-white transition-all outline-none
                        ${maxError ? "ring-2 ring-[#ba1a1a]" : ""}`}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#3e494a]/50 font-bold pointer-events-none">
            {unit}
          </span>
        </div>
      </div>
      {(minError || maxError) && (
        <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{minError || maxError}</p>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function FacilityConfigCreate({ embedded = false, onCancel, onSuccess } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location.state?.mode === "edit";
  const editData = location.state?.data ?? {};

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [imageError, setImageError] = useState("");
  const [facilityData, setFacilityData] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);

  // ── Load all cities on mount ──────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCityList({ companyID }))
      .then((res) => setCityOptions(toOptions(toArray(res), "City_Name", "CityID")))
      .catch(() => {});
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch full record when editing ────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editData?.id) return;
    dispatch(fetchFacilities({ ID: editData.id, companyID: companyID }))
      .then((res) => {
        const result = toArray(res);
        if (result.length > 0) setFacilityData(result[0]);
      });
  }, [isEditMode, editData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const existingImagePath = facilityData?.FacilityImagePath ?? editData?.imagePath ?? null;
  const existingImageName = existingImagePath
    ? existingImagePath.split("/").pop().replace(/^[0-9a-f-]{36}_/i, "")
    : null;

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    name: editData.name ?? "",
    cityId: editData.cityId ?? "",
    tempMin: editData.tempMin ?? "",
    tempMax: editData.tempMax ?? "",
    humidMin: editData.humidityMin ?? "",
    humidMax: editData.humidityMax ?? "",
    description: editData.description ?? "",
    image: null,
  });

  // ── Sync cityId from facilityData ─────────────────────────────────────────
  useEffect(() => {
    if (!facilityData) return;
    const cityId = facilityData.CityID ?? facilityData.City_Id ?? facilityData.cityId ?? "";
    if (cityId) setForm((prev) => ({ ...prev, cityId: String(cityId) }));
  }, [facilityData]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const PAIRED = { tempMin: "tempMax", tempMax: "tempMin", humidMin: "humidMax", humidMax: "humidMin" };

  const handleField = useCallback((field) => (e) => {
    const raw = e.target.value;
    const val = field === "name" ? sanitizeLabName(raw) : raw;
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => {
      const next = { ...prev, [field]: "" };
      if (PAIRED[field]) next[PAIRED[field]] = "";
      return next;
    });
    if (field === "name") setSaveError(null);
    setSaveSuccess(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDropdown = useCallback((field) => (val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSaveSuccess(false);
  }, []);

  // ── Image handler with type + 5MB restriction ────────────────────────────
  const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
  const handleImage = useCallback((file) => {
    if (!file) {
      setImageError("");
      setForm((prev) => ({ ...prev, image: null }));
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Only PNG, JPG, JPEG, and SVG files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image file size must be under 5 MB. Please choose a smaller file.");
      return;
    }
    setImageError("");
    setForm((prev) => ({ ...prev, image: file }));
    setErrors((prev) => ({ ...prev, image: "" }));
    setSaveSuccess(false);
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.name.trim())    errs.name     = "Lab Name is required.";
    if (!form.cityId)         errs.cityId   = "Location (City) is required.";
    if (form.tempMin === "")  errs.tempMin  = "Temperature min is required.";
    if (form.tempMax === "")  errs.tempMax  = "Temperature max is required.";
    if (form.tempMin !== "" && Number(form.tempMin) < -100) errs.tempMin = "Temperature cannot be below -100°C.";
    if (form.tempMax !== "" && Number(form.tempMax) >  500) errs.tempMax = "Temperature cannot exceed 500°C.";
    if (!errs.tempMin && !errs.tempMax && form.tempMin !== "" && form.tempMax !== "" && Number(form.tempMin) >= Number(form.tempMax))
                              errs.tempMin  = "Min must be less than Max.";
    if (form.humidMin === "") errs.humidMin = "Humidity min is required.";
    if (form.humidMax === "") errs.humidMax = "Humidity max is required.";
    if (form.humidMin !== "" && Number(form.humidMin) < 0)   errs.humidMin = "Humidity cannot be below 0%.";
    if (form.humidMax !== "" && Number(form.humidMax) > 100) errs.humidMax = "Humidity cannot exceed 100%.";
    if (!errs.humidMin && !errs.humidMax && form.humidMin !== "" && form.humidMax !== "" && Number(form.humidMin) >= Number(form.humidMax))
                              errs.humidMin = "Min must be less than Max.";
    return errs;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const formData = new FormData();
    formData.append("Facilities.FacilityID",        isEditMode ? (editData.id ?? 0) : 0);
    formData.append("Facilities.FacilityName",      normalizeSpaces(form.name));
    formData.append("Facilities.Description",       normalizeSpaces(form.description ?? ""));
    formData.append("Facilities.TemperatureMin",    Number(form.tempMin));
    formData.append("Facilities.TemperatureMax",    Number(form.tempMax));
    formData.append("Facilities.HumidityMin",       Number(form.humidMin));
    formData.append("Facilities.HumidityMax",       Number(form.humidMax));
    formData.append("Facilities.SortOrder",         0);
    formData.append("Facilities.CityID",            form.cityId ?? 0);
    formData.append("CreatedBy",                    employeeID);
    formData.append("CompanyID",                    companyID);

    if (form.image) {
      formData.append("facilityImage", form.image);
      formData.append("Facilities.FacilityImagePath", "");
    } else if (isEditMode && existingImagePath) {
      try {
        const res  = await fetch(`${URL}${existingImagePath}`);
        const blob = await res.blob();
        const name = existingImagePath.split("/").pop();
        formData.append("facilityImage", blob, name);
      } catch {
        formData.append("facilityImage", new Blob([], { type: "image/png" }), "empty.png");
      }
      formData.append("Facilities.FacilityImagePath", existingImagePath);
    } else {
      formData.append("facilityImage", new Blob([], { type: "image/png" }), "empty.png");
      formData.append("Facilities.FacilityImagePath", "");
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    dispatch(saveFacility(formData))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;
        if (Number(result) === -1) {
          setSaveError("A Facility with this name already exists.");
        } else if (Number(result) > 0) {
          setSaveSuccess(true);
          const msg = isEditMode ? "Facility updated successfully." : "Facility created successfully.";
          setSuccessMsg(msg);
          setTimeout(() => setSuccessMsg(""), 4000);
          if (embedded) {
            // ── EMBEDDED: hand the new id back to the caller and let them close ──
            onSuccess?.(Number(result), normalizeSpaces(form.name));
          } else if (isEditMode) {
            navigate("/application-config", { state: { tab: "facility", refresh: true, editSuccess: true } });
          } else {
            setForm(EMPTY_FORM);
            setErrors({});
            setTimeout(() => setSaveSuccess(false), 4000);
          }
        } else {
          setSaveError("Failed to save. Please try again.");
        }
      })
      .catch((err) => setSaveError(err?.message ?? "Failed to save facility"))
      .finally(() => setIsSaving(false));
  }, [form, isEditMode, editData, existingImagePath, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = useCallback(
    () => (embedded ? onCancel?.() : navigate("/application-config", { state: { tab: "facility", refresh: true } })),
    [navigate, embedded, onCancel]
  );

  const saveLabel = isEditMode ? "Update Facility" : "Save Facility";

  const SaveButton = () => (
    <button
      onClick={handleSave}
      disabled={isSaving}
      className="px-8 py-3 rounded-xl bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSaving
        ? <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
        : <><span className="material-symbols-outlined text-[18px]">save</span>{saveLabel}</>}
    </button>
  );

  const Wrapper = embedded ? React.Fragment : DashboardLayout;

  return (
    <Wrapper>
      <div className="px-4 py-5 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        <div>

          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-5">
            <div>
              {!embedded && (
                <>
                  <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
                    <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">
                      App Configuration
                    </button>
                    <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                    <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">
                      Facility Management
                    </button>
                    <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                    <span className="text-[#08757c] font-semibold">
                      {isEditMode ? "Edit Facility" : "Add New Facility"}
                    </span>
                  </nav>
                  <CancelButton onClick={handleCancel} label="Back to List" />
                </>
              )}
              <h2 className="text-3xl font-extrabold tracking-tight">
                {isEditMode ? "Edit Facility" : "Add New Facility"}
              </h2>
              <p className="text-[#3e494a] text-sm mt-1 max-w-xl">
                Define and configure your facility details. Set environment thresholds and upload facility information to keep your lab records organized.
              </p>
            </div>
            <div className={`flex gap-3 pt-4 ${embedded ? "mr-12" : ""}`}>
              <CancelButton onClick={handleCancel} />
              <SaveButton />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Main Form ── */}
            <div className="lg:col-span-2">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#bdc9c9]/20">
                <div className="space-y-5">

                  {/* Error Banner */}
                  {saveError && (
                    <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-5 py-4 text-sm flex items-center gap-3">
                      <span className="material-symbols-outlined text-[20px]">error</span>
                      {saveError}
                    </div>
                  )}

                  {/* Success Banner */}
                  {successMsg && (
                    <div className="bg-[#bbf46e]/20 text-black rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                      <span className="material-symbols-outlined text-[20px] text-black">check_circle</span>
                      {successMsg}
                    </div>
                  )}

                  {/* Lab Name */}
                  <div>
                    <FieldLabel required>Lab Name</FieldLabel>
                    <TextInput
                      placeholder="e.g. Molecular Biology Wing A"
                      value={form.name}
                      onChange={handleField("name")}
                      error={errors.name}
                      maxLength={NAME_MAX_LENGTH}
                    />
                    <CharCounter value={form.name} max={NAME_MAX_LENGTH} />
                  </div>

                  {/* Location (City) */}
                  <div>
                    <FieldLabel required>Location (City)</FieldLabel>
                    <Dropdown
                      options={cityOptions}
                      value={form.cityId}
                      onChange={handleDropdown("cityId")}
                      placeholder="Select City"
                      searchPlaceholder="Search city..."
                      error={errors.cityId}
                    />
                  </div>

                  {/* Temperature Range */}
                  <div>
                    <FieldLabel required>Temperature Range</FieldLabel>
                    <NumberRangeInput
                      minValue={form.tempMin}
                      maxValue={form.tempMax}
                      onMinChange={handleField("tempMin")}
                      onMaxChange={handleField("tempMax")}
                      unit="°C"
                      minError={errors.tempMin}
                      maxError={errors.tempMax}
                      rangeMin={-100}
                      rangeMax={500}
                    />
                  </div>

                  {/* Humidity Range */}
                  <div>
                    <FieldLabel required>Humidity Range</FieldLabel>
                    <NumberRangeInput
                      minValue={form.humidMin}
                      maxValue={form.humidMax}
                      onMinChange={handleField("humidMin")}
                      onMaxChange={handleField("humidMax")}
                      unit="%"
                      minError={errors.humidMin}
                      maxError={errors.humidMax}
                      rangeMin={0}
                      rangeMax={100}
                    />
                  </div>

                  {/* ── Facility Image ── */}
                  <div>
                    <FieldLabel>Facility Image</FieldLabel>

                    {/* Recommendations panel */}
                    <div className="mb-4 bg-[#f7fafa] rounded-xl p-4 border border-[#e5e9e9]">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[#3e494a] flex items-center gap-1 mb-3">
                        <span className="material-symbols-outlined text-[14px] text-[#08757c]">tips_and_updates</span>
                        Image Recommendations
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { icon: "straighten", label: "Minimum Size", value: "400 × 560 px" },
                          { icon: "aspect_ratio", label: "Ideal Size", value: "600 × 840 px" },
                          { icon: "photo_size_select_actual", label: "Max File Size", value: "5 MB" },
                          { icon: "palette", label: "Formats", value: "PNG · JPG · JPEG · SVG" },
                          { icon: "crop_portrait", label: "Orientation", value: "Portrait (2:3)" },
                          { icon: "center_focus_strong", label: "Focus", value: "Center of image" },
                        ].map(({ icon, label, value }) => (
                          <div key={label} className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-[#08757c] text-[16px]">{icon}</span>
                            <div>
                              <p className="text-[9px] font-bold text-[#3e494a]/60 uppercase tracking-widest">{label}</p>
                              <p className="text-xs font-semibold text-[#181c1d]">{value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#ba1a1a] flex items-start gap-1 mt-3 pt-3 border-t border-[#e5e9e9]">
                        <span className="material-symbols-outlined text-[13px] flex-shrink-0 mt-0.5">warning</span>
                        Use a portrait image with the lab clearly visible and centered. Avoid screenshots or dark/blurry photos.
                      </p>
                    </div>

                    {/* Existing image preview in edit mode */}
                    {isEditMode && existingImagePath && !form.image && (
                      <div className="mb-3 space-y-2">
                        <a
                          href={`${URL}${existingImagePath}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3 bg-[#f1f4f4] border border-[#bdc9c9]/30 px-3 py-2 rounded-lg w-fit hover:bg-[#e5e9e9] hover:border-[#08757c]/30 transition-colors group"
                        >
                          <img
                            src={`${URL}${existingImagePath}`}
                            alt={existingImageName ?? "facility image"}
                            className="w-10 h-10 rounded-lg flex-shrink-0 border border-[#bdc9c9]/30 bg-[#f7fafa]"
                            style={{ objectFit: "contain" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              if (e.target.nextSibling) e.target.nextSibling.style.display = "inline";
                            }}
                          />
                          <span className="material-symbols-outlined text-[18px] text-[#08757c]" style={{ display: "none" }}>image</span>
                          <span className="text-xs font-medium text-[#3e494a] truncate max-w-xs group-hover:text-[#08757c] transition-colors">
                            {existingImageName}
                          </span>
                          <span className="material-symbols-outlined text-[14px] text-[#3e494a]/40 group-hover:text-[#08757c] transition-colors">
                            open_in_new
                          </span>
                        </a>
                        <p className="text-[10px] text-[#3e494a]/60">
                          Upload a new image below to replace the existing one.
                        </p>
                      </div>
                    )}

                    <FileUpload
                      value={form.image}
                      onChange={handleImage}
                      error={errors.image}
                      accept="image/png,image/jpg,image/jpeg,image/svg+xml"
                      hint="PNG · JPG · JPEG · SVG · Portrait 2:3 · Min 400×560px · Max 5MB"
                    />
                    {imageError && (
                      <p className="text-xs text-[#ba1a1a] mt-2 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">error</span>
                        {imageError}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <FieldLabel>Facility Description</FieldLabel>
                    <Textarea
                      id="facility-description"
                      value={form.description}
                      onChange={handleField("description")}
                      placeholder="Detailed equipment manifest, primary research focus, and specialized safety protocols..."
                      rows={4}
                      maxLength={DESC_MAX_LENGTH}
                    />
                    <CharCounter value={form.description} max={DESC_MAX_LENGTH} />
                  </div>

                  {/* Info note */}
                  <div className="flex items-center gap-3 p-4 bg-[#f1f4f4] rounded-xl border-l-4 border-[#08757c]">
                    <span className="material-symbols-outlined text-[#08757c] text-[20px]">info</span>
                    <p className="text-xs text-[#3e494a] font-medium">
                      Lab Name, Location, Temperature Range, and Humidity Range are mandatory fields.
                    </p>
                  </div>

                  {/* Success Banner */}
                  {saveSuccess && (
                    <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                      <span className="material-symbols-outlined text-[20px] text-[#436900]">check_circle</span>
                      <span className="font-semibold">
                        {isEditMode
                          ? "Facility updated successfully."
                          : "Facility created successfully. You can add another one below."}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <CancelButton onClick={handleCancel} />
                <SaveButton />
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div>
              <div className="bg-gradient-to-br from-[#005b60] to-[#08757c] p-5 rounded-2xl text-white relative overflow-hidden shadow-lg shadow-[#005b60]/20">
                <div className="relative z-10">
                  <span className="material-symbols-outlined text-[32px] mb-2 block opacity-90">settings</span>
                  <h3 className="text-base font-extrabold mb-1.5">Configuration Guide</h3>
                  <p className="text-xs opacity-85 leading-relaxed mb-4">
                    Facility configuration defines the physical environment standards for each lab unit within your organization.
                  </p>
                  <ul className="space-y-2 text-xs opacity-80">
                    {[
                      "Select a city to associate this facility with a location",
                      "Set accurate temperature and humidity ranges",
                      "Use sort order to control display sequence",
                      "Upload a landscape facility image for easy identification",
                      "Description helps staff identify lab purpose",
                    ].map((tip) => (
                      <li key={tip} className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#bbf46e] rounded-full opacity-20 blur-3xl pointer-events-none" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </Wrapper>
  );
}
